import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { DbQuotation, DbQuotationSystem, DbQuotationItem } from '../types';
import { logger } from '../lib/logger';
import { useTeam } from '../contexts/TeamContext';
import { withDerivedTotalsAll } from '../utils/quotationTotals';

export function useQuotations() {
  const { currentTeam } = useTeam();
  const [rawQuotations, setQuotations] = useState<DbQuotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // #78: `total_price` / `total_cost` are dead columns — written as 0 at
  // creation and never updated again, so every screen reading them showed
  // ₪0.00 for every quotation. Derive them here, once, and every consumer of
  // this hook (quotations list, dashboard tiles, project detail grid) reads a
  // live figure from one place.
  //
  // Deriving over the state rather than inside `fetchQuotations` is what makes
  // this drift-proof: the optimistic add/update/delete paths below all write
  // through `setQuotations`, so they recompute for free and there is no write
  // path anyone can forget.
  const quotations = useMemo(
    () => withDerivedTotalsAll(rawQuotations),
    [rawQuotations]
  );

  // Fetch all quotations with their systems and items
  const fetchQuotations = useCallback(async () => {
    if (!currentTeam) {
      setQuotations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('quotations')
        .select(
          `
          *,
          quotation_systems (
            *,
            quotation_items (
              *,
              component:components (*)
            )
          )
        `
        )
        .eq('team_id', currentTeam.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setQuotations(data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch quotations'
      );
    } finally {
      setLoading(false);
    }
  }, [currentTeam]);

  // Add a new quotation
  const addQuotation = async (
    quotation: Omit<DbQuotation, 'id' | 'created_at' | 'updated_at' | 'team_id'>
  ) => {
    if (!currentTeam) {
      logger.error('Cannot add quotation: No active team');
      throw new Error('No active team');
    }

    try {
      setError(null);

      const quotationWithTeam = {
        ...quotation,
        team_id: currentTeam.id,
      };

      logger.debug('Adding quotation with team_id:', {
        team_id: currentTeam.id,
        quotation_number: quotation.quotation_number,
      });

      const { data, error } = await supabase
        .from('quotations')
        .insert([quotationWithTeam])
        .select(
          `
          *,
          quotation_systems (
            *,
            quotation_items (
              *,
              component:components (*)
            )
          )
        `
        )
        .single();

      if (error) {
        logger.error('Quotation insert error:', error);
        throw error;
      }

      setQuotations(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add quotation');
      throw err;
    }
  };

  // Update an existing quotation
  const updateQuotation = async (id: string, updates: Partial<DbQuotation>) => {
    if (!currentTeam) throw new Error('No active team');

    // #144: reverting a quotation to draft retracts the price confirmation.
    //
    // 'draft' is the only status that makes a quotation editable again
    // (isDbQuotationEditable), so it is the only one that can invalidate the
    // confirmation: from that moment the rep may change the items, and the
    // price someone affirmed for the previous send is no longer the price this
    // quotation computes. #77 skips the confirmation dialog whenever
    // price_confirmed_at is set, so leaving it behind re-sends a stale number
    // in silence.
    //
    // Here rather than at the status dropdown because this is the single
    // function every quotation write in the app goes through — a second caller
    // that reverts a status cannot forget to do it. `price_confirmed_by` is
    // deliberately absent: stamp_price_confirmed_by() nulls it whenever
    // price_confirmed_at goes null, and an audit field naming an actor must
    // not be written by the client (see the #77 migration).
    //
    // Real nulls, not `undefined`: PostgREST omits undefined keys from the
    // patch, so an undefined here would leave the confirmation in the row and
    // the clearing would silently undo itself on the next load.
    const effectiveUpdates: Partial<DbQuotation> =
      updates.status === 'draft'
        ? {
            ...updates,
            quoted_price_ils: null,
            system_price_ils: null,
            price_confirmed_at: null,
          }
        : updates;

    try {
      setError(null);
      logger.info('🔵 useQuotations - updateQuotation called:', {
        id,
        updates: JSON.stringify(effectiveUpdates),
        teamId: currentTeam.id,
      });

      const { data, error } = await supabase
        .from('quotations')
        .update(effectiveUpdates)
        .eq('id', id)
        .eq('team_id', currentTeam.id) // Ensure team ownership
        .select()
        .single();

      if (error) {
        logger.error('🔴 useQuotations - Database update FAILED:', error);
        throw error;
      }

      logger.info('🟢 useQuotations - Database updated successfully:', {
        returnedData: JSON.stringify(data),
        exchange_rate: data?.exchange_rate,
        eur_to_ils_rate: data?.eur_to_ils_rate,
      });

      // Only update the changed fields, preserve nested quotation_systems
      setQuotations(prev => {
        const updated = prev.map(quot => {
          if (quot.id === id) {
            // Merge only the updated fields, don't overwrite nested data.
            // effectiveUpdates, not updates — otherwise the row in state keeps
            // the price confirmation the database has just cleared (#144).
            const merged = { ...quot, ...effectiveUpdates };
            logger.debug('useQuotations - Merging quotation:', {
              old: quot,
              updates: effectiveUpdates,
              merged,
            });
            return merged;
          }
          return quot;
        });
        logger.debug('useQuotations - State updated with new quotations array');
        return updated;
      });
      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update quotation'
      );
      throw err;
    }
  };

  // Delete a quotation
  const deleteQuotation = async (id: string) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      // .select() forces PostgREST to return the affected rows. If RLS
      // silently filters the row out (no error, 0 affected), data is [] and
      // we can detect the failure instead of showing a phantom delete.
      const { data, error } = await supabase
        .from('quotations')
        .delete()
        .eq('id', id)
        .eq('team_id', currentTeam.id)
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error(
          'Delete blocked by row-level security or row not found'
        );
      }

      setQuotations(prev => prev.filter(quot => quot.id !== id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete quotation'
      );
      throw err;
    }
  };

  // Get a single quotation with all related data
  const getQuotation = async (id: string) => {
    if (!currentTeam) return null;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('quotations')
        .select(
          `
          *,
          quotation_systems (
            *,
            quotation_items (
              *,
              component:components (*)
            )
          )
        `
        )
        .eq('id', id)
        .eq('team_id', currentTeam.id) // Ensure team ownership
        .single();

      if (error) throw error;

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch quotation'
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Add a system to a quotation.
  //
  // `created_by` is omitted alongside `team_id` on purpose (#120): the database
  // defaults it to auth.uid() and pins it against later UPDATEs, and it decides
  // who may delete the row. A caller that could supply it could hand its own
  // system to somebody else, or — via duplicateQuotation, which copies field by
  // field from a source row — quietly carry a colleague's id onto the copy.
  const addQuotationSystem = async (
    system: Omit<
      DbQuotationSystem,
      'id' | 'created_at' | 'updated_at' | 'team_id' | 'created_by'
    >
  ) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      const systemWithTeam = {
        ...system,
        team_id: currentTeam.id,
      };

      const { data, error } = await supabase
        .from('quotation_systems')
        .insert([systemWithTeam])
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setQuotations(prev =>
        prev.map(quot =>
          quot.id === system.quotation_id
            ? {
                ...quot,
                quotation_systems: [...(quot.quotation_systems || []), data],
              }
            : quot
        )
      );

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to add quotation system'
      );
      throw err;
    }
  };

  // Update a quotation system
  const updateQuotationSystem = async (
    id: string,
    updates: Partial<DbQuotationSystem>
  ) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      const { data, error } = await supabase
        .from('quotation_systems')
        .update(updates)
        .eq('id', id)
        .eq('team_id', currentTeam.id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setQuotations(prev =>
        prev.map(quot => ({
          ...quot,
          quotation_systems:
            quot.quotation_systems?.map(sys =>
              sys.id === id ? { ...sys, ...data } : sys
            ) || [],
        }))
      );

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update quotation system'
      );
      throw err;
    }
  };

  // Delete a quotation system
  const deleteQuotationSystem = async (id: string) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      const { data, error } = await supabase
        .from('quotation_systems')
        .delete()
        .eq('id', id)
        .eq('team_id', currentTeam.id)
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error(
          'Delete blocked by row-level security or row not found'
        );
      }

      // Update local state
      setQuotations(prev =>
        prev.map(quot => ({
          ...quot,
          quotation_systems:
            quot.quotation_systems?.filter(sys => sys.id !== id) || [],
        }))
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete quotation system'
      );
      throw err;
    }
  };

  // Add an item to a quotation system
  const addQuotationItem = async (
    item: Omit<DbQuotationItem, 'id' | 'created_at' | 'updated_at' | 'team_id'>
  ) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      const itemWithTeam = {
        ...item,
        team_id: currentTeam.id,
      };

      const { data, error } = await supabase
        .from('quotation_items')
        .insert([itemWithTeam])
        .select(
          `
          *,
          component:components (*)
        `
        )
        .single();

      if (error) throw error;

      // Update local state
      setQuotations(prev =>
        prev.map(quot => ({
          ...quot,
          quotation_systems:
            quot.quotation_systems?.map(sys =>
              sys.id === item.quotation_system_id
                ? {
                    ...sys,
                    quotation_items: [...(sys.quotation_items || []), data],
                  }
                : sys
            ) || [],
        }))
      );

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to add quotation item'
      );
      throw err;
    }
  };

  // Update a quotation item
  const updateQuotationItem = async (
    id: string,
    updates: Partial<DbQuotationItem>
  ) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      const { data, error } = await supabase
        .from('quotation_items')
        .update(updates)
        .eq('id', id)
        .eq('team_id', currentTeam.id)
        .select(
          `
          *,
          component:components (*)
        `
        )
        .single();

      if (error) throw error;

      // Update local state
      setQuotations(prev =>
        prev.map(quot => ({
          ...quot,
          quotation_systems:
            quot.quotation_systems?.map(sys => ({
              ...sys,
              quotation_items:
                sys.quotation_items?.map(item =>
                  item.id === id ? { ...item, ...data } : item
                ) || [],
            })) || [],
        }))
      );

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update quotation item'
      );
      throw err;
    }
  };

  // Delete a quotation item
  const deleteQuotationItem = async (id: string) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      const { data, error } = await supabase
        .from('quotation_items')
        .delete()
        .eq('id', id)
        .eq('team_id', currentTeam.id)
        .select('id');

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error(
          'Delete blocked by row-level security or row not found'
        );
      }

      // Update local state
      setQuotations(prev =>
        prev.map(quot => ({
          ...quot,
          quotation_systems:
            quot.quotation_systems?.map(sys => ({
              ...sys,
              quotation_items:
                sys.quotation_items?.filter(item => item.id !== id) || [],
            })) || [],
        }))
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete quotation item'
      );
      throw err;
    }
  };

  // Duplicate quotation with all systems and items
  const duplicateQuotation = async (
    sourceQuotationId: string,
    newQuotationNumber: string,
    newVersion?: number,
    /**
     * #153: set by the new-version path, left undefined by the copy path.
     *
     * A copy is its own quotation and supersedes nothing; a version
     * replaces the row it came from, and the dashboard has to be able to
     * tell which without guessing. The distinction is known here, at
     * creation, and nowhere else afterwards -- which is why it is written
     * down rather than inferred later from version numbers.
     */
    supersedesQuotationId?: string
  ) => {
    if (!currentTeam) throw new Error('No active team');

    try {
      setError(null);

      // Fetch full quotation with systems and items
      const sourceQuotation = await getQuotation(sourceQuotationId);
      if (!sourceQuotation) throw new Error('Source quotation not found');

      // Create new quotation (preserving project_id link)
      const newQuotation = await addQuotation({
        quotation_number: newQuotationNumber,
        version: newVersion || 1,
        supersedes_quotation_id: supersedesQuotationId,
        customer_name: sourceQuotation.customer_name,
        customer_email: sourceQuotation.customer_email,
        // #63: project_name used to get " (עותק)" appended here. The grids
        // render project_name as the project column, so a duplicate read as
        // if it belonged to a different project — the exact complaint in the
        // ticket, and an inconsistency with "new version", which never
        // renamed it. The copy is identified by its quotation_number; the
        // project it belongs to must stay untouched.
        project_name: sourceQuotation.project_name,
        project_description: sourceQuotation.project_description,
        project_id: sourceQuotation.project_id, // Preserve project link
        currency: sourceQuotation.currency,
        // #143: every per-quotation pricing parameter, and the reason this is
        // an explicit list rather than three extra lines.
        //
        // Only exchange_rate and margin_percentage used to be copied. The
        // other five were absent from the insert — and these columns carry
        // non-null defaults in Postgres (eur_to_ils_rate 4.0,
        // risk_percentage 5.0, day_work_cost 1200.00), so the copy was not
        // left empty to be filled in later: it was stamped with values that
        // have nothing to do with the source. A duplicate then priced itself
        // off those, and its total differed from the quotation it was copied
        // from. On live data 7 of 8 quotations carry a non-default EUR rate
        // and day-work cost, so this fired on almost every duplicate.
        //
        // The authoritative list is the payload the parameters panel writes —
        // `dbUpdates` in useQuotationActions.saveParameters, mirrored by
        // buildNewQuotationPayload. These seven are that list. An eighth
        // parameter added there has to be added here too.
        exchange_rate: sourceQuotation.exchange_rate,
        eur_to_ils_rate: sourceQuotation.eur_to_ils_rate,
        margin_percentage: sourceQuotation.margin_percentage,
        risk_percentage: sourceQuotation.risk_percentage,
        day_work_cost: sourceQuotation.day_work_cost,
        // #77: the customer discount is per-quotation and changes the net
        // price, so a copy that dropped it quoted a different number. Copied
        // verbatim, null included — this column is explicitly nullable and
        // null is the real "no discount" value.
        discount_type: sourceQuotation.discount_type,
        discount_value: sourceQuotation.discount_value,
        // Quotation-level MSRP toggle. Nothing in src/ reads this column today
        // (the toggle that prices a line is the per-item one), but it is a
        // declared per-quotation pricing field with a DB default of false, so
        // omitting it would silently reset a source that had it set.
        use_msrp_pricing: sourceQuotation.use_msrp_pricing,
        status: 'draft',
        total_cost: 0,
        total_price: 0,
        terms: sourceQuotation.terms,
        notes: sourceQuotation.notes,
        valid_until_date: sourceQuotation.valid_until_date,
        // Deliberately NOT copied, so this stays a decision rather than an
        // omission:
        //   quoted_price_ils, system_price_ils, price_confirmed_at,
        //   price_confirmed_by — #77's audit trail of one confirmation by one
        //   person. The copy has never been sent, and priceConfirmation is
        //   keyed on price_confirmed_at rather than on status, so copying it
        //   would show a confirmed price on a brand-new draft.
        //   created_by — the BEFORE INSERT authorship trigger (#119) requires
        //   it to be the authenticated user; the duplicator owns the copy.
        //   follow_up_date, priority, status_changed_at — workflow tracking on
        //   the source, not pricing.
      });

      if (!newQuotation) throw new Error('Failed to create quotation');

      // #63: from here on the copy exists in the database. Any failure while
      // copying systems or items has to remove it again — otherwise a failed
      // duplicate leaves a quotation with no items sitting in the list,
      // looking like a real (empty) quotation. That is what the ticket
      // reporter hit: the duplicate "failed", and the husk showed up anyway.
      try {
        if (sourceQuotation.quotation_systems) {
          for (const sourceSystem of sourceQuotation.quotation_systems) {
            const newSystem = await addQuotationSystem({
              quotation_id: newQuotation.id,
              system_name: sourceSystem.system_name,
              system_description: sourceSystem.system_description,
              quantity: sourceSystem.quantity,
              unit_cost: sourceSystem.unit_cost,
              total_cost: sourceSystem.total_cost,
              margin_percentage: sourceSystem.margin_percentage,
              unit_price: sourceSystem.unit_price,
              total_price: sourceSystem.total_price,
              sort_order: sourceSystem.sort_order,
            });

            // The items below reference this system by id. Copying them
            // against a system that didn't come back is what produces the
            // quotation_items_system_team_fk violation, so stop here and say
            // which system it was rather than letting the FK report it.
            if (!newSystem?.id) {
              throw new Error(
                `Failed to copy system "${sourceSystem.system_name}" (source id ${sourceSystem.id}) — no row returned, so its items cannot be linked`
              );
            }

            for (const sourceItem of sourceSystem.quotation_items || []) {
              await addQuotationItem({
                quotation_system_id: newSystem.id,
                component_id: sourceItem.component_id,
                assembly_id: sourceItem.assembly_id,
                item_name: sourceItem.item_name,
                manufacturer: sourceItem.manufacturer,
                manufacturer_part_number: sourceItem.manufacturer_part_number,
                item_type: sourceItem.item_type,
                labor_subtype: sourceItem.labor_subtype,
                quantity: sourceItem.quantity,
                unit_cost: sourceItem.unit_cost,
                total_cost: sourceItem.total_cost,
                margin_percentage: sourceItem.margin_percentage,
                unit_price: sourceItem.unit_price,
                total_price: sourceItem.total_price,
                original_currency: sourceItem.original_currency,
                original_cost: sourceItem.original_cost,
                notes: sourceItem.notes,
                sort_order: sourceItem.sort_order,
                // #63: these were missing, so a copy silently lost them. The
                // worst of them is is_custom_item: without it a duplicated
                // custom item is treated as library-backed, which is exactly
                // the "double-click does nothing" defect from #54, reappearing
                // in every copy.
                is_custom_item: sourceItem.is_custom_item,
                labor_type_id: sourceItem.labor_type_id,
                is_internal_labor: sourceItem.is_internal_labor,
                msrp_price: sourceItem.msrp_price,
                msrp_currency: sourceItem.msrp_currency,
                use_msrp_pricing: sourceItem.use_msrp_pricing,
                partner_discount_percent: sourceItem.partner_discount_percent,
              });
            }
          }
        }
      } catch (copyError) {
        // Best-effort rollback. If this delete also fails there is nothing
        // further to try, so report the original failure either way — it is
        // the one that explains what went wrong.
        try {
          await supabase
            .from('quotations')
            .delete()
            .eq('id', newQuotation.id)
            .eq('team_id', currentTeam.id);
        } catch (rollbackError) {
          logger.error(
            'Duplicate failed AND the partial copy could not be removed',
            { quotationId: newQuotation.id, rollbackError }
          );
        }
        throw copyError;
      }

      // Fetch the complete new quotation
      return await getQuotation(newQuotation.id);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to duplicate quotation'
      );
      throw err;
    }
  };

  // Load quotations on mount and when team changes
  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Refetch quotations when window/tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchQuotations();
      }
    };

    const handleFocus = () => {
      fetchQuotations();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchQuotations]);

  return {
    quotations,
    loading,
    error,
    fetchQuotations,
    getQuotation,
    addQuotation,
    updateQuotation,
    deleteQuotation,
    addQuotationSystem,
    updateQuotationSystem,
    deleteQuotationSystem,
    addQuotationItem,
    updateQuotationItem,
    deleteQuotationItem,
    duplicateQuotation,
  };
}
